import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  updatePasswordRequest,
  clearUpdatePasswordSuccess,
} from "../../../redux/action";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
// import { BottomNav } from "../../../components/Nav/BottomNav"; // Ensure this matches your path

// --- Reusable Password Input matching the new design ---
const PasswordField = ({ label, name, placeholder, formik }) => {
  const [showPassword, setShowPassword] = useState(false);
  const hasError = formik.touched[name] && formik.errors[name];

  return (
    <div className="w-full mb-5">
      <label className="block text-[14px] font-semibold text-[#071330] mb-2">
        {label}
      </label>
      <div
        className={`relative flex items-center border rounded-lg bg-[#f8faff] transition-all overflow-hidden ${
          hasError
            ? "border-red-300 focus-within:border-red-500 focus-within:bg-white"
            : "border-[#dce5fc] focus-within:border-[#1a4bdf] focus-within:bg-white shadow-[0_2px_10px_rgba(26,75,223,0.02)]"
        }`}
      >
        {/* Left Lock Icon */}
        <div
          className={`pl-4 pr-3 flex items-center justify-center ${hasError ? "text-red-400" : "text-[#1a4bdf]"}`}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>
        </div>

        {/* Input Field - 16px to prevent iOS Safari Zoom */}
        <input
          type={showPassword ? "text" : "password"}
          name={name}
          placeholder={placeholder}
          {...formik.getFieldProps(name)}
          className="flex-1 py-2.5 bg-transparent text-[16px] text-slate-800 placeholder-slate-400 outline-none w-full"
        />

        {/* Right Eye Toggle */}
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="pr-4 pl-2 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors outline-none"
        >
          {showPassword ? (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
              <line x1="1" y1="1" x2="23" y2="23"></line>
            </svg>
          ) : (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
              <circle cx="12" cy="12" r="3"></circle>
            </svg>
          )}
        </button>
      </div>

      {hasError && (
        <div className="mt-1.5 text-[12px] text-red-500 font-medium px-1">
          {formik.errors[name]}
        </div>
      )}
    </div>
  );
};

const Security = () => {
  const dispatch = useDispatch();

  const history = useHistory();

  const {
    updatePasswordSuccess,
    updatePasswordError,
    updatePasswordLoading,
    token,
  } = useSelector((state) => ({
    updatePasswordSuccess: state?.ajaxStatuses?.success?.updatePassword,
    updatePasswordError: state?.ajaxStatuses?.errors?.updatePassword,
    updatePasswordLoading: state?.loadingIndicator?.updatePasswordLoading,
    token: state?.userData?.token,
  }));

  const formik = useFormik({
    initialValues: {
      password: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: yup.object({
      password: yup.string().required("Current password is required"),
      newPassword: yup
        .string()
        .min(8, "Password must be at least 8 characters")
        .required("New Password is required"),
      confirmPassword: yup
        .string()
        .oneOf([yup.ref("newPassword"), null], "Passwords do not match")
        .required("Confirm password is required"),
    }),
    onSubmit: (values) => {
      const { confirmPassword, ...submitData } = values;
      dispatch(updatePasswordRequest({ formData: submitData, token }));
    },
  });

  useEffect(() => {
    if (updatePasswordError) {
      toast.error(updatePasswordError);
    }
  }, [updatePasswordError]);

  useEffect(() => {
    if (updatePasswordSuccess) {
      toast.success("Password updated successfully");
      formik.resetForm();
      dispatch(clearUpdatePasswordSuccess());
    }
  }, [updatePasswordSuccess, dispatch, formik]);

  return (
    /* Locked to w-[428px] to replicate the mobile app view exactly.
     */
    <div className="w-[428px] mx-auto bg-[#f4f7ff] min-h-screen relative font-sans text-slate-900 pb-32 shadow-[0_0_50px_rgba(0,0,0,0.1)] overflow-x-hidden">
      {/* --- Top Blue Header --- */}
      <div
        className="absolute top-0 left-0 w-full h-[260px] bg-[#022881] z-0 overflow-hidden bg-no-repeat bg-right-top"
        style={{
          backgroundImage: "url('/img/new/background/africa-map.jpeg')",
          backgroundSize: "contain",
        }}
      >
        {/* Back Button */}
        <div className="relative z-10 px-6 pt-12 pb-2">
          <button
            onClick={history.goBack}
            className="flex items-center gap-2 text-white font-medium text-[15px] active:scale-95 transition-transform"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            Back
          </button>
        </div>

        {/* Title Area */}
        <div className="relative z-10 px-6 pt-2">
          <h1 className="text-[28px] font-semibold text-white mb-1">
            Change Password
          </h1>
          <p className="text-[13px] text-blue-100 font-medium">
            Keep your account secure by using a strong password
          </p>
        </div>
      </div>

      <div className="relative z-10 mt-[160px] px-4 flex flex-col gap-4">
        {/* --- Main Form Card --- */}
        <div className="bg-white rounded-[2rem] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-50">
          <form onSubmit={formik.handleSubmit}>
            <PasswordField
              label="Current Password"
              name="password"
              placeholder="Enter your existing account password."
              formik={formik}
            />

            <div className="h-px w-full bg-gray-100 my-6"></div>

            <PasswordField
              label="New Password"
              name="newPassword"
              placeholder="Enter a new password for your account."
              formik={formik}
            />

            <PasswordField
              label="Confirm New Password"
              name="confirmPassword"
              placeholder="Re-enter the new password to confirm."
              formik={formik}
            />

            {/* Submit Button */}
            <div className="mt-4">
              <button
                type="button"
                onClick={formik.handleSubmit}
                disabled={
                  !(formik.dirty && formik.isValid) || updatePasswordLoading
                }
                className="w-full bg-[#0642cc] hover:bg-blue-800 disabled:bg-[#a5bcf6] disabled:cursor-not-allowed text-white rounded-xl py-4 flex items-center justify-center gap-2 relative shadow-lg shadow-blue-600/20 transition-all active:scale-[0.98]"
              >
                {updatePasswordLoading ? (
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                ) : (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect
                      x="3"
                      y="11"
                      width="18"
                      height="11"
                      rx="2"
                      ry="2"
                    ></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                )}
                <span className="text-[16px] font-semibold">
                  {updatePasswordLoading ? "Updating..." : "Update Password"}
                </span>
              </button>
            </div>
          </form>
        </div>

        {/* --- Password Requirements Card --- */}
        <div className="bg-white rounded-[1.5rem] p-6 shadow-[0_4px_20px_rgb(0,0,0,0.02)] border border-gray-100 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="text-[#1a4bdf]">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
              </div>
              <h3 className="text-[15px] font-semibold text-slate-900">
                Password Requirements
              </h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-center gap-2.5">
                <div className="w-5 h-5 rounded-full bg-[#10b981] flex items-center justify-center text-white shrink-0 shadow-sm">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <span className="text-[13px] text-slate-600 font-medium">
                  Minimum 8 characters long
                </span>
              </li>
              <li className="flex items-center gap-2.5">
                <div className="w-5 h-5 rounded-full bg-[#10b981] flex items-center justify-center text-white shrink-0 shadow-sm">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <span className="text-[13px] text-slate-600 font-medium">
                  At least one number
                </span>
              </li>
              <li className="flex items-center gap-2.5">
                <div className="w-5 h-5 rounded-full bg-[#10b981] flex items-center justify-center text-white shrink-0 shadow-sm">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <span className="text-[13px] text-slate-600 font-medium">
                  At least one special character
                </span>
              </li>
            </ul>
          </div>
          {/* 3D Shield Placeholder */}
          <div className="w-24 shrink-0 flex justify-end">
            <img
              src="/img/new/background/shield.jpeg"
              alt="Security Shield"
              className="w-full object-contain rounded-full"
            />
          </div>
        </div>

        {/* --- Security Tip Card --- */}
        <div className="bg-[#f4f7ff] rounded-[1.5rem] p-5 border border-[#dce5fc] flex items-center gap-4">
          <div className="flex-1">
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-[#1a4bdf] flex items-center justify-center text-white shrink-0 mt-0.5 shadow-sm">
                <span className="text-[12px] font-semibold font-serif italic">
                  i
                </span>
              </div>
              <p className="text-[12px] text-slate-600 font-medium leading-relaxed">
                <span className="font-semibold text-slate-800">
                  Security Tip:
                </span>{" "}
                For your protection, ensure your new password is kept
                confidential and not shared with any third party. If you
                encounter any issues while updating your password, our 24/7
                WhatsApp support team is available to assist you.
              </p>
            </div>
          </div>
          {/* 3D Headset Placeholder */}
          <div className="w-16 shrink-0 flex justify-end">
            <img
              src="/img/new/background/shieldandwhatsapp.jpeg"
              alt="Support Headset"
              className="w-full object-contain rounded-full"
            />
          </div>
        </div>
      </div>

      {/* --- Floating Chat Button --- */}
      <button className="fixed bottom-[100px] right-6 z-50 bg-[#1a4bdf] hover:bg-blue-700 text-white px-4 py-3 rounded-full flex items-center gap-2.5 shadow-[0_10px_25px_rgba(26,75,223,0.3)] transition-transform active:scale-95">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
          <circle cx="9" cy="12" r="1" fill="currentColor"></circle>
          <circle cx="12" cy="12" r="1" fill="currentColor"></circle>
          <circle cx="15" cy="12" r="1" fill="currentColor"></circle>
        </svg>
        <div className="flex flex-col items-start pr-1">
          <span className="text-[13px] font-semibold leading-none">
            Chat with us
          </span>
          <span className="text-[10px] text-blue-100 font-medium">
            We're online!
          </span>
        </div>

        {/* Green Online Dot */}
        <div className="absolute -bottom-1 -left-1 w-4 h-4 bg-[#10b981] rounded-full border-[3px] border-white"></div>
      </button>

      {/* Insert your actual bottom nav component here if needed, or leave it out if the global layout handles it */}
      {/* <div className="fixed bottom-0 w-[428px] z-40 bg-white">
        <BottomNav />
      </div> */}
    </div>
  );
};

export default Security;
